/**
 * Client wrapper around the KEY2 Worker API.
 * `/api/*` is proxied to the Worker by Vite (vite.config.ts) in dev,
 * and served from the same origin in production via Cloudflare Pages.
 */

import type {
  Language,
  Session,
  Unit,
} from './types';
import type { PriceBreakdown, YieldBreakdown } from './pricing';

export interface SharePayload {
  session: Session;
  unit: Unit;
  breakdown: PriceBreakdown;
  yieldData: YieldBreakdown;
  language: Language;
  /**
   * V3 D6.1 — selected payment plan from V2FinancePage. Optional so
   * v1 ShareModal posts the same shape unchanged. The Worker uses
   * the presence of this field as the v2-PDF discriminator: stored
   * in the KV record, then `record.v2PaymentPlanId !== undefined`
   * means the headless browser hits `/r2/{id}` (the v2 print route)
   * instead of `/r/{id}` (the v1 layout).
   */
  v2PaymentPlanId?: 'cash' | 'monthly' | 'quarterly' | 'flexible';
}

export interface ShareResponse {
  sessionId: string;
  sessionUrl: string;
  /**
   * Phase 8f-bcs B.2 (2026-05-08) — server-side PDF generation.
   * Real https://assets.almoftah.om URL when generation succeeded;
   * `null` if BROWSER/R2 binding missing or generation failed (see
   * `pdfError` for the reason). The QR fallback always works.
   */
  pdfUrl: string | null;
  pdfError: string | null;
  /**
   * B.4 (2026-05-08) — Template-based WhatsApp send via respond.io.
   *   • status='sent'   → template accepted by respond.io
   *   • status='failed' → respond.io rejected (template/field error, etc.)
   *   • status='mocked' → no PDF or RESPOND_* secrets missing
   *
   * `assigneeSet` means the conversation was routed to the advisor
   * picked in Step 1. Non-critical — `false` with `assigneeError` set
   * is still a usable outcome; the advisor can claim the conversation
   * manually from respond.io.
   */
  whatsapp: {
    status: 'sent' | 'failed' | 'mocked';
    messageId?: string | null;
    contactId?: string | null;
    tagAdded?: boolean;
    tagError?: string | null;
    assigneeSet?: boolean;
    assigneeError?: string | null;
    error?: string | null;
  };
  /** B.4 — advisor that owns the lead (for the success message in ShareModal). */
  advisor?: {
    name: string;
    respondId: number;
  };
  /** Reports whether the Worker stored to real KV or the dev memory fallback. */
  backend: 'kv' | 'memory';
}

/**
 * Stored shape returned by GET /api/sessions/{id}. Wraps the original
 * SharePayload with the metadata the Worker added at write time.
 */
export interface SharedSessionRecord {
  sessionId: string;
  createdAt: number;
  language: Language;
  session: Session;
  unit: Unit;
  breakdown: PriceBreakdown;
  yieldData: YieldBreakdown;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let code = 'http_error';
    let message = `Request failed: ${res.status}`;
    try {
      const errBody = (await res.json()) as { error?: string; message?: string };
      if (errBody.error) code = errBody.error;
      if (errBody.message) message = errBody.message;
    } catch {
      // body wasn't JSON — keep defaults
    }
    throw new ApiError(res.status, code, message);
  }
  return (await res.json()) as T;
}

export function shareSession(payload: SharePayload): Promise<ShareResponse> {
  return postJson<ShareResponse>('/api/sessions/share', payload);
}

/**
 * Fetch a previously-shared session record by id. Throws ApiError(404, …)
 * when the id is unknown or expired.
 */
export async function fetchSharedSession(
  sessionId: string
): Promise<SharedSessionRecord> {
  const res = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}`);
  if (!res.ok) {
    let code = 'http_error';
    let message = `Request failed: ${res.status}`;
    try {
      const errBody = (await res.json()) as { error?: string; message?: string };
      if (errBody.error) code = errBody.error;
      if (errBody.message) message = errBody.message;
    } catch {
      // body wasn't JSON — keep defaults
    }
    throw new ApiError(res.status, code, message);
  }
  return (await res.json()) as SharedSessionRecord;
}
